import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { foldersService, CreateFolderRequest, UpdateFolderRequest } from '@/services/folders';
import { Folder, FolderEntityType } from '@/components/AiChat/ai-chat/types';

interface FoldersState {
  folders: Folder[];
  expandedFolders: string[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: FoldersState = {
  folders: [],
  expandedFolders: [],
  status: 'idle',
  error: null,
};

// Async thunks
export const fetchFolders = createAsyncThunk(
  'folders/fetchFolders',
  async (payload: { topicId: string; entityType?: FolderEntityType }) => {
    const { topicId, entityType } = payload;
    if (!topicId) {
      console.warn('fetchFolders called without topicId');
      return [];
    }
    return await foldersService.fetchFolders(topicId, entityType);
  }
);

export const fetchWorkspaceFolders = createAsyncThunk(
  'folders/fetchWorkspaceFolders',
  async (payload: { workspaceId: string; entityType?: FolderEntityType }) => {
    const { workspaceId, entityType } = payload;
    if (!workspaceId) {
      console.warn('fetchWorkspaceFolders called without workspaceId');
      return [];
    }
    return await foldersService.fetchWorkspaceFolders(workspaceId, entityType);
  }
);

export const createFolder = createAsyncThunk(
  'folders/createFolder',
  async (payload: CreateFolderRequest) => {
    return await foldersService.createFolder(payload);
  }
);

export const updateFolder = createAsyncThunk(
  'folders/updateFolder',
  async (payload: { id: string; data: UpdateFolderRequest }) => {
    return await foldersService.updateFolder(payload.id, payload.data);
  }
);

export const deleteFolder = createAsyncThunk(
  'folders/deleteFolder',
  async (payload: { id: string }) => {
    await foldersService.deleteFolder(payload.id);
    return payload.id;
  }
);

export const moveItemToFolder = createAsyncThunk(
  'folders/moveItemToFolder',
  async (payload: { entityType: 'datasource' | 'tamed_data' | 'artifact'; itemId: string; folderId: string | null }) => {
    await foldersService.moveItemToFolder(payload.entityType, payload.itemId, payload.folderId);
    return payload;
  }
);

export const moveItemsToFolderBatch = createAsyncThunk(
  'folders/moveItemsToFolderBatch',
  async (payload: { entityType: 'datasource' | 'tamed_data' | 'artifact'; itemIds: string[]; folderId: string | null }) => {
    await foldersService.moveItemsToFolderBatch(payload.entityType, payload.itemIds, payload.folderId);
    return payload;
  }
);

// Helper function to build folder tree structure
const buildFolderTree = (folders: Folder[]): Folder[] => {
  const folderMap = new Map<string, Folder>();
  const rootFolders: Folder[] = [];

  // First pass: create map of all folders
  folders.forEach(folder => {
    folderMap.set(folder.id, { ...folder, children: [] });
  });

  // Second pass: build tree structure
  folders.forEach(folder => {
    const folderWithChildren = folderMap.get(folder.id)!;
    if (folder.fk_parent_id && folderMap.has(folder.fk_parent_id)) {
      const parent = folderMap.get(folder.fk_parent_id)!;
      parent.children = parent.children || [];
      parent.children.push(folderWithChildren);
    } else {
      rootFolders.push(folderWithChildren);
    }
  });

  return rootFolders;
};

const foldersSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    setFolders: (state, action: PayloadAction<Folder[]>) => {
      state.folders = action.payload;
    },
    addFolder: (state, action: PayloadAction<Folder>) => {
      state.folders.push(action.payload);
    },
    updateFolderInState: (state, action: PayloadAction<Folder>) => {
      const index = state.folders.findIndex(f => f.id === action.payload.id);
      if (index !== -1) {
        state.folders[index] = action.payload;
      }
    },
    removeFolderFromState: (state, action: PayloadAction<string>) => {
      state.folders = state.folders.filter(f => f.id !== action.payload);
    },
    toggleFolderExpanded: (state, action: PayloadAction<string>) => {
      const index = state.expandedFolders.indexOf(action.payload);
      if (index !== -1) {
        state.expandedFolders.splice(index, 1);
      } else {
        state.expandedFolders.push(action.payload);
      }
    },
    setFolderExpanded: (state, action: PayloadAction<{ folderId: string; expanded: boolean }>) => {
      const { folderId, expanded } = action.payload;
      const index = state.expandedFolders.indexOf(folderId);
      if (expanded && index === -1) {
        state.expandedFolders.push(folderId);
      } else if (!expanded && index !== -1) {
        state.expandedFolders.splice(index, 1);
      }
    },
    clearFolders: (state) => {
      state.folders = [];
      state.expandedFolders = [];
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchFolders (topic-level)
    builder.addCase(fetchFolders.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(fetchFolders.fulfilled, (state, action) => {
      state.status = 'succeeded';
      const { topicId, entityType } = action.meta.arg;
      // Keep workspace folders and topic folders from other topics/entity types
      const keepFolders = state.folders.filter(f => {
        // Keep workspace folders
        if (f.fk_workspace_id) return true;
        // Remove topic folders that match the fetched topic and entity type
        if (f.fk_topic_id === topicId && (!entityType || f.entity_type === entityType)) return false;
        return true;
      });

      // De-duplicate: remove any folders from keepFolders that are in the new payload
      const newFolderIds = new Set(action.payload.map(f => f.id));
      const dedupedKeepFolders = keepFolders.filter(f => !newFolderIds.has(f.id));

      state.folders = [...dedupedKeepFolders, ...action.payload];
    });
    builder.addCase(fetchFolders.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || null;
    });

    // fetchWorkspaceFolders (workspace-level)
    builder.addCase(fetchWorkspaceFolders.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(fetchWorkspaceFolders.fulfilled, (state, action) => {
      state.status = 'succeeded';
      const { workspaceId, entityType } = action.meta.arg;
      // Keep topic folders and workspace folders from other workspaces/entity types
      const keepFolders = state.folders.filter(f => {
        // Keep topic folders
        if (f.fk_topic_id) return true;
        // Remove workspace folders that match the fetched workspace and entity type
        if (f.fk_workspace_id === workspaceId && (!entityType || f.entity_type === entityType)) return false;
        return true;
      });

      // De-duplicate: remove any folders from keepFolders that are in the new payload
      const newFolderIds = new Set(action.payload.map(f => f.id));
      const dedupedKeepFolders = keepFolders.filter(f => !newFolderIds.has(f.id));

      state.folders = [...dedupedKeepFolders, ...action.payload];
    });
    builder.addCase(fetchWorkspaceFolders.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || null;
    });

    // createFolder
    builder.addCase(createFolder.fulfilled, (state, action) => {
      state.folders.push(action.payload);
    });

    // updateFolder
    builder.addCase(updateFolder.fulfilled, (state, action) => {
      const index = state.folders.findIndex(f => f.id === action.payload.id);
      if (index !== -1) {
        state.folders[index] = action.payload;
      }
    });

    // deleteFolder
    builder.addCase(deleteFolder.fulfilled, (state, action) => {
      state.folders = state.folders.filter(f => f.id !== action.payload);
      state.expandedFolders = state.expandedFolders.filter(id => id !== action.payload);
    });
  },
});

export const {
  setFolders,
  addFolder,
  updateFolderInState,
  removeFolderFromState,
  toggleFolderExpanded,
  setFolderExpanded,
  clearFolders,
} = foldersSlice.actions;

// Selectors
export const selectFolders = (state: { folders: FoldersState }) => state.folders.folders;
export const selectFolderTree = (state: { folders: FoldersState }) => buildFolderTree(state.folders.folders);
export const selectExpandedFolders = (state: { folders: FoldersState }) => state.folders.expandedFolders;
export const selectFoldersStatus = (state: { folders: FoldersState }) => state.folders.status;
export const selectFoldersError = (state: { folders: FoldersState }) => state.folders.error;

export const selectFoldersByEntityType = (entityType: FolderEntityType) =>
  (state: { folders: FoldersState }) =>
    state.folders.folders.filter(f => f.entity_type === entityType);

export const selectFolderTreeByEntityType = (entityType: FolderEntityType) =>
  (state: { folders: FoldersState }) =>
    buildFolderTree(state.folders.folders.filter(f => f.entity_type === entityType));

export default foldersSlice.reducer;
